/* eslint-disable unicorn/no-null, no-plusplus, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unnecessary-condition, unicorn/prevent-abbreviations, @typescript-eslint/only-throw-error, no-magic-numbers, @typescript-eslint/no-non-null-assertion, eqeqeq, sonarjs/different-types-comparison */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import useDesignerFilesStore from './DesignerFiles.store';
import { designerV2API } from '@/services/designer/designerV2Client';
import { gcsClient } from '@/utils/api/gcsClient.service';
import type { GCSObject } from '@/utils/api/gcsClient.types';
import {
    finishProgressNotification,
    showProgressNotification
} from '@/utils/ui';

const buildOptimisticGCSObject = (path: string): GCSObject => ({
    name: path,
    size: '0',
    contentType: '',
    updated: new Date().toISOString()
});

const FOLDER_MARKER_NAME = '.gitkeep';
const isFolderMarker = (path: string): boolean =>
    path === FOLDER_MARKER_NAME || path.endsWith(`/${FOLDER_MARKER_NAME}`);

const getSessionPrefix = (): string => {
    const raw = gcsClient.getPrefix().replace(/^\//, '');
    return raw.endsWith('/') ? raw : `${raw}/`;
};

const stripSessionPrefix = (gcsKey: string, prefix: string): string | null => {
    if (!gcsKey.startsWith(prefix)) {
        return null;
    }
    return gcsKey.slice(prefix.length);
};

async function runWithConcurrency<T>(
    items: readonly T[],
    limit: number,
    task: (item: T) => Promise<void>
): Promise<void> {
    if (items.length === 0) {
        return;
    }
    let nextIndex = 0;
    let firstError: unknown = null;
    const worker = async (): Promise<void> => {
        while (nextIndex < items.length && firstError === null) {
            const currentIndex = nextIndex++;
            try {
                await task(items[currentIndex]);
            } catch (error) {
                if (firstError === null) {
                    firstError = error;
                }
            }
        }
    };
    const workerCount = Math.min(limit, items.length);
    const workers: Promise<void>[] = [];
    for (let i = 0; i < workerCount; i++) workers.push(worker());
    await Promise.all(workers);
    if (firstError !== null) {
        throw firstError;
    }
}

export const FILE_QUERY_KEYS = {
    fileList: (versionId: string) => ['gcs-files', versionId] as const,
    fileContent: (versionId: string, path: string) =>
        ['gcs-file-content', versionId, path] as const
};

export const useFileListQuery = (versionId: string | null) => {
    return useQuery({
        queryKey: FILE_QUERY_KEYS.fileList(versionId ?? ''),
        queryFn: async () => {
            const prefix = getSessionPrefix();
            const result = await gcsClient.listObjects();
            const items = result.items ?? [];
            return items
                .map((it) => {
                    const name = stripSessionPrefix(it.name, prefix);
                    return name && name.length > 0 ? { ...it, name } : null;
                })
                .filter((it): it is GCSObject => it !== null);
        },
        enabled: Boolean(versionId && gcsClient.isInitialized())
    });
};

type TFileContentResult = {
    path: string;
    content: string;
    generation: string | null;
};

export class FileSaveConflictError extends Error {
    public readonly path: string;
    public constructor(path: string) {
        super(`Save conflict for ${path}`);
        this.name = 'FileSaveConflictError';
        this.path = path;
    }
}

const isPreconditionFailed = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const status = (error as { response?: { status?: unknown } }).response
        ?.status;
    return status === 412;
};

export const useFileContentQuery = (
    versionId: string | null,
    path: string | null
) => {
    return useQuery<TFileContentResult>({
        queryKey: FILE_QUERY_KEYS.fileContent(versionId ?? '', path ?? ''),
        queryFn: async () => {
            const { content, generation } = await gcsClient.getObject(path!);
            return { path: path!, content, generation };
        },
        enabled: Boolean(versionId && path && gcsClient.isInitialized()),

        staleTime: 5 * 60 * 1000
    });
};

type TSaveFileVariables = {
    path: string;
    content: string;
    baseGeneration?: string | null;
    force?: boolean;
};

export const useSaveFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            path,
            content,
            baseGeneration,
            force
        }: TSaveFileVariables) => {
            try {
                const { generation } = await gcsClient.uploadObject(
                    path,
                    content,
                    {
                        ifGenerationMatch:
                            force || baseGeneration == undefined
                                ? undefined
                                : baseGeneration
                    }
                );
                return { path, content, generation };
            } catch (error) {
                if (isPreconditionFailed(error)) {
                    throw new FileSaveConflictError(path);
                }
                throw error;
            }
        },
        onSuccess: (result, { path }) => {
            const versionId =
                useDesignerFilesStore.getState().selectedVersionId;
            if (versionId) {
                queryClient.setQueryData<TFileContentResult>(
                    FILE_QUERY_KEYS.fileContent(versionId, path),
                    {
                        path,
                        content: result.content,
                        generation: result.generation
                    }
                );
            }
            const store = useDesignerFilesStore.getState();
            store.markDirty(path, false);
            store.setBaseGeneration(path, result.generation);
            notifications.show({ color: 'green', message: 'File saved.' });
        },
        onError: (error) => {
            if (error instanceof FileSaveConflictError) {
                return;
            }
            notifications.show({
                color: 'red',
                message:
                    'Could not save file. Check your connection and try again.'
            });
        }
    });
};

export const useCreateFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ path }: { path: string }) => {
            const { generation } = await gcsClient.uploadObject(path, '', {
                ifGenerationMatch: 0
            });
            return { generation };
        },
        onMutate: async ({ path }) => {
            const isFolder = isFolderMarker(path);
            const notificationId = `create-${isFolder ? 'folder' : 'file'}-${path}`;
            showProgressNotification(
                notificationId,
                isFolder ? 'Creating folder' : 'Creating file',
                isFolder
                    ? 'Adding folder to the version…'
                    : 'Adding file to the version…'
            );
            const versionId =
                useDesignerFilesStore.getState().selectedVersionId;
            if (!versionId) {
                return { notificationId, isFolder };
            }
            const queryKey = FILE_QUERY_KEYS.fileList(versionId);
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<GCSObject[]>(queryKey);
            const placeholder = buildOptimisticGCSObject(path);
            queryClient.setQueryData<GCSObject[]>(queryKey, (old) => [
                placeholder,
                ...(old ?? [])
            ]);
            return { previous, notificationId, versionId, isFolder };
        },
        onSuccess: (result, { path }, ctx) => {
            const versionId =
                ctx?.versionId ??
                useDesignerFilesStore.getState().selectedVersionId;
            if (versionId) {
                queryClient.setQueryData<TFileContentResult>(
                    FILE_QUERY_KEYS.fileContent(versionId, path),
                    { path, content: '', generation: result.generation }
                );
            }
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    ctx.isFolder ? 'Folder created' : 'File created'
                );
            }
        },
        onError: (error, _vars, ctx) => {
            const collision =
                typeof error === 'object' &&
                error !== null &&
                (error as { response?: { status?: number } }).response
                    ?.status === 412;
            const isFolder = ctx?.isFolder ?? false;
            const noun = isFolder ? 'folder' : 'file';
            const Noun = isFolder ? 'Folder' : 'File';
            const title = collision
                ? `${Noun} already exists`
                : `Could not create ${noun}`;
            const message = collision
                ? `A ${noun} with this name already exists.`
                : `Could not create ${noun}. Please try again.`;
            if (ctx?.previous && ctx.versionId) {
                queryClient.setQueryData(
                    FILE_QUERY_KEYS.fileList(ctx.versionId),
                    ctx.previous
                );
            }
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    title,
                    message
                );
            } else {
                notifications.show({
                    color: 'red',
                    title,
                    message
                });
            }
        },
        onSettled: (_data, _error, _vars, ctx) => {
            if (ctx?.versionId) {
                void queryClient.invalidateQueries({
                    queryKey: FILE_QUERY_KEYS.fileList(ctx.versionId)
                });
            }
        }
    });
};

export const useDeleteFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ path }: { path: string }) =>
            gcsClient.deleteObject(path),
        onMutate: ({ path }) => {
            const versionId =
                useDesignerFilesStore.getState().selectedVersionId;
            useDesignerFilesStore.getState().addPendingDelete(path);
            const notificationId = `delete-file-${path}`;
            showProgressNotification(
                notificationId,
                'Deleting file',
                'Removing file from the version…'
            );
            return { notificationId, versionId };
        },
        onSuccess: (_result, { path }, ctx) => {
            const versionId =
                ctx?.versionId ??
                useDesignerFilesStore.getState().selectedVersionId;
            if (versionId) {
                queryClient.setQueryData<GCSObject[]>(
                    FILE_QUERY_KEYS.fileList(versionId),
                    (old) => (old ?? []).filter((o) => o.name !== path)
                );

                queryClient.removeQueries({
                    queryKey: FILE_QUERY_KEYS.fileContent(versionId, path)
                });
                void queryClient.invalidateQueries({
                    queryKey: FILE_QUERY_KEYS.fileList(versionId)
                });
            }
            useDesignerFilesStore.getState().closeFile(path);
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    'File deleted'
                );
            }
        },
        onError: (_error, _vars, ctx) => {
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    'Could not delete file',
                    'Please try again.'
                );
            } else {
                notifications.show({
                    color: 'red',
                    message: 'Could not delete file. Please try again.'
                });
            }
        },
        onSettled: (_data, _error, { path }) => {
            useDesignerFilesStore.getState().removePendingDelete(path);
        }
    });
};

export const useRenameFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            oldPath,
            newPath
        }: {
            oldPath: string;
            newPath: string;
        }) => {
            const client = gcsClient;
            const { content } = await client.getObject(oldPath);
            await client.uploadObject(newPath, content);
            await client.deleteObject(oldPath);
        },
        onMutate: async ({ oldPath, newPath }) => {
            const queryKey = FILE_QUERY_KEYS.fileList(
                useDesignerFilesStore.getState().selectedVersionId ?? ''
            );
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<GCSObject[]>(queryKey);
            queryClient.setQueryData<GCSObject[]>(queryKey, (old) =>
                (old ?? []).map((item) =>
                    item.name === oldPath ? { ...item, name: newPath } : item
                )
            );
            return { previous };
        },
        onSuccess: (_, { oldPath, newPath }) => {
            const state = useDesignerFilesStore.getState();
            const versionId = state.selectedVersionId;
            if (versionId) {
                const cached = queryClient.getQueryData<TFileContentResult>(
                    FILE_QUERY_KEYS.fileContent(versionId, oldPath)
                );
                if (cached !== undefined) {
                    queryClient.setQueryData<TFileContentResult>(
                        FILE_QUERY_KEYS.fileContent(versionId, newPath),
                        {
                            path: newPath,
                            content: cached.content,
                            generation: null
                        }
                    );
                }
                queryClient.removeQueries({
                    queryKey: FILE_QUERY_KEYS.fileContent(versionId, oldPath)
                });
            }

            if (state.openFiles[oldPath]) {
                state.closeFile(oldPath);
            }
        },
        onError: (_error, _vars, ctx) => {
            const queryKey = FILE_QUERY_KEYS.fileList(
                useDesignerFilesStore.getState().selectedVersionId ?? ''
            );
            if (ctx?.previous) {
                queryClient.setQueryData(queryKey, ctx.previous);
            }
            notifications.show({
                color: 'red',
                message: 'Could not rename file. Please try again.'
            });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: FILE_QUERY_KEYS.fileList(
                    useDesignerFilesStore.getState().selectedVersionId ?? ''
                )
            });
        }
    });
};

export const useRenameFolderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            oldPath,
            newPath
        }: {
            oldPath: string;
            newPath: string;
        }) => {
            const client = gcsClient;
            const prefix = getSessionPrefix();

            const result = await client.listObjects(`${oldPath}/`);
            const items = result.items ?? [];
            if (items.length === 0) {
                return;
            }

            const pairs: { src: string; dest: string }[] = [];
            for (const item of items) {
                const relativeOld = stripSessionPrefix(item.name, prefix);
                if (!relativeOld) {
                    continue;
                }
                const relativeNew = `${newPath}${relativeOld.slice(oldPath.length)}`;
                pairs.push({ src: item.name, dest: relativeNew });
            }
            if (pairs.length === 0) {
                return;
            }

            await runWithConcurrency(pairs, 10, async (pair) =>
                client.copyObject(pair.src, pair.dest)
            );

            await runWithConcurrency(pairs, 10, async (pair) =>
                client.deleteObject(pair.src)
            );
        },
        onMutate: async ({ oldPath, newPath }) => {
            const queryKey = FILE_QUERY_KEYS.fileList(
                useDesignerFilesStore.getState().selectedVersionId ?? ''
            );
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<GCSObject[]>(queryKey);
            queryClient.setQueryData<GCSObject[]>(queryKey, (old) =>
                (old ?? []).map((item) => {
                    if (
                        item.name === oldPath ||
                        item.name.startsWith(`${oldPath}/`)
                    ) {
                        return {
                            ...item,
                            name: `${newPath}${item.name.slice(oldPath.length)}`
                        };
                    }
                    return item;
                })
            );
            return { previous };
        },
        onSuccess: (_, { oldPath, newPath }) => {
            const state = useDesignerFilesStore.getState();
            const versionId = state.selectedVersionId;
            if (versionId) {
                const oldKeyRoot = ['gcs-file-content', versionId] as const;
                const matches = queryClient.getQueriesData<TFileContentResult>({
                    queryKey: oldKeyRoot
                });
                for (const [key, cached] of matches) {
                    const cachedPath = key[2] as string | undefined;
                    if (cached === undefined || !cachedPath) {
                        continue;
                    }
                    if (
                        cachedPath === oldPath ||
                        cachedPath.startsWith(`${oldPath}/`)
                    ) {
                        const renamed = `${newPath}${cachedPath.slice(oldPath.length)}`;

                        queryClient.setQueryData<TFileContentResult>(
                            FILE_QUERY_KEYS.fileContent(versionId, renamed),
                            {
                                path: renamed,
                                content: cached.content,
                                generation: null
                            }
                        );
                        queryClient.removeQueries({
                            queryKey: FILE_QUERY_KEYS.fileContent(
                                versionId,
                                cachedPath
                            )
                        });
                    }
                }
            }

            for (const p of Object.keys(state.openFiles)) {
                if (p === oldPath || p.startsWith(`${oldPath}/`)) {
                    state.closeFile(p);
                }
            }
        },
        onError: (_error, _vars, ctx) => {
            const queryKey = FILE_QUERY_KEYS.fileList(
                useDesignerFilesStore.getState().selectedVersionId ?? ''
            );
            if (ctx?.previous) {
                queryClient.setQueryData(queryKey, ctx.previous);
            }
            notifications.show({
                color: 'red',
                message: 'Could not rename folder. Please try again.'
            });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: FILE_QUERY_KEYS.fileList(
                    useDesignerFilesStore.getState().selectedVersionId ?? ''
                )
            });
        }
    });
};

export const useDeleteFolderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ path }: { path: string }) => {
            const client = gcsClient;

            const result = await client.listObjects(`${path}/`);
            const items = result.items ?? [];
            if (items.length === 0) {
                return;
            }
            await runWithConcurrency(items, 10, async (item) =>
                client.deleteObject(item.name)
            );
        },
        onMutate: ({ path }) => {
            const versionId =
                useDesignerFilesStore.getState().selectedVersionId;
            useDesignerFilesStore.getState().addPendingDelete(path);
            const notificationId = `delete-folder-${path}`;
            showProgressNotification(
                notificationId,
                'Deleting folder',
                'Removing folder and contents…'
            );
            return { notificationId, versionId };
        },
        onSuccess: (_result, { path }, ctx) => {
            const state = useDesignerFilesStore.getState();
            const versionId = ctx?.versionId ?? state.selectedVersionId;
            if (versionId) {
                const prefix = `${path}/`;
                queryClient.setQueryData<GCSObject[]>(
                    FILE_QUERY_KEYS.fileList(versionId),
                    (old) =>
                        (old ?? []).filter(
                            (o) => o.name !== path && !o.name.startsWith(prefix)
                        )
                );

                const matches = queryClient.getQueriesData<TFileContentResult>({
                    queryKey: ['gcs-file-content', versionId] as const
                });
                for (const [key] of matches) {
                    const cachedPath = key[2] as string | undefined;
                    if (!cachedPath) {
                        continue;
                    }
                    if (
                        cachedPath === path ||
                        cachedPath.startsWith(`${path}/`)
                    ) {
                        queryClient.removeQueries({
                            queryKey: FILE_QUERY_KEYS.fileContent(
                                versionId,
                                cachedPath
                            )
                        });
                    }
                }
            }
            for (const p of Object.keys(state.openFiles)) {
                if (p.startsWith(`${path}/`)) {
                    state.closeFile(p);
                }
            }
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    'Folder deleted'
                );
            }
        },
        onError: (_error, _vars, ctx) => {
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    'Could not delete folder',
                    'Please try again.'
                );
            } else {
                notifications.show({
                    color: 'red',
                    message: 'Could not delete folder. Please try again.'
                });
            }
        },
        onSettled: (_data, _error, { path }, ctx) => {
            useDesignerFilesStore.getState().removePendingDelete(path);
            if (ctx?.versionId) {
                void queryClient.invalidateQueries({
                    queryKey: FILE_QUERY_KEYS.fileList(ctx.versionId)
                });
            }
        }
    });
};

const isNotFound = (error: unknown): boolean =>
    (error as { response?: { status?: number } } | null)?.response?.status ===
    404;

export const useMintSessionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (versionId: string) =>
            designerV2API.mintFileAccessSession(versionId, {
                noErrorMessage: true
            }),
        onMutate: (versionId) => ({
            requestId: gcsClient.beginMint(versionId)
        }),
        onSuccess: (session, versionId, ctx) => {
            if (!ctx || !gcsClient.finishMint(versionId, ctx.requestId)) {
                return;
            }
            gcsClient.init({
                session,
                onTokenExpired: async () =>
                    designerV2API.mintFileAccessSession(versionId)
            });
            const store = useDesignerFilesStore.getState();
            store.clearOpenFiles();
            store.setSelectedVersionId(versionId);
        },
        onError: (error, versionId, ctx) => {
            if (!ctx || !gcsClient.finishMint(versionId, ctx.requestId)) {
                return;
            }
            if (isNotFound(error)) {
                void queryClient.invalidateQueries({
                    queryKey: ['project-versions']
                });
                notifications.show({
                    color: 'red',
                    message:
                        'This version no longer exists. Refreshing versions…'
                });
                return;
            }
            notifications.show({
                color: 'red',
                message: 'Could not open version files. Please try again.'
            });
        }
    });
};
